import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Print as PrintIcon,
  CleaningServices as CleaningIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import usePrinterStatus from '../../hooks/usePrinterStatus';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import PrinterCleaningGuideModal from '../PrinterCleaningGuideModal';

const translateCleaningType = cleaningType => {
  switch (cleaningType) {
    case 'small':
      return 'Kleine Reinigung';
    case 'big':
      return 'Große Reinigung';
  }
};

function PrinterOverview() {
  const theme = useTheme();
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaningDialogOpen, setCleaningDialogOpen] = useState(false);
  const [cancelCleaningDialogOpen, setCancelCleaningDialogOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(null);

  const { checkRightAny } = useAuth();
  const canStartCleaning = checkRightAny?.('printerStatus:special:startCleaning');
  const { getAllPrinterStatuses, startCleaning, cancelCleaning, isLoading, error } = usePrinterStatus();

  const fetchPrinters = async () => {
    try {
      setLoading(true);
      const data = await getAllPrinterStatuses();
      setPrinters(data);
    } catch (err) {
      console.error('Error fetching printers:', err);
      toast.error('Fehler beim Laden der Drucker-Status');
    } finally {
      setLoading(false);
    }
  };

  // TODO dont fetch all data -> consider merging endpoints for less requests
  const refreshPrintersSilently = async () => {
    try {
      const data = await getAllPrinterStatuses();
      setPrinters(data);
    } catch (err) {
      console.error('Silent refresh failed:', err);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchPrinters();
      toast.success('Drucker-Status aktualisiert');
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setRefreshing(false);
    }
  };

  const handleStartCleaning = async printerName => {
    try {
      await startCleaning(printerName);
      toast.success(`Reinigung für ${printerName} gestartet`);
      // Refresh the data to show updated status
      await fetchPrinters();
    } catch (err) {
      console.error('Error starting cleaning:', err);
      toast.error(`Fehler beim Starten der Reinigung für ${printerName}`);
    }
  };

  const handleOpenCancelCleaningDialog = printer => {
    setSelectedPrinter(printer);
    setCancelCleaningDialogOpen(true);
  };

  const handleCloseCancelCleaningDialog = () => {
    setCancelCleaningDialogOpen(false);
    setSelectedPrinter(null);
  };

  const handleConfirmCancelCleaning = async () => {
    if (selectedPrinter) {
      try {
        await cancelCleaning(selectedPrinter.name);
        toast.success(`Reinigung für ${selectedPrinter.name} abgebrochen`);
        // Refresh the data to show updated status
        await fetchPrinters();
        handleCloseCancelCleaningDialog();
      } catch (err) {
        console.error('Error canceling cleaning:', err);
        toast.error(`Fehler beim Abbrechen der Reinigung für ${selectedPrinter.name}`);
      }
    }
  };

  const handleOpenCleaningDialog = printer => {
    setSelectedPrinter(printer);
    setCleaningDialogOpen(true);
  };

  const handleCloseCleaningDialog = () => {
    setCleaningDialogOpen(false);
    setSelectedPrinter(null);
  };

  const handleConfirmCleaning = async () => {
    if (selectedPrinter) {
      await handleStartCleaning(selectedPrinter.name);
    }
  };

  useEffect(() => {
    const selectedName = selectedPrinter?.name;
    if (!selectedName) return;
    const updated = printers.find(p => p?.name === selectedName);
    if (updated) setSelectedPrinter(updated);
  }, [printers, selectedPrinter?.name]);

  useEffect(() => {
    fetchPrinters();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!refreshing && !isLoading) {
        refreshPrintersSilently();
      }
    }, 10000);
    return () => clearInterval(intervalId);
  }, [refreshing, isLoading]);

  const isPrinterOffline = printer => {
    // Determine offline state if there is no update or the last update is older than 5 minutes
    if (!printer || !printer.lastUpdated) return true;
    const lastUpdateMs = new Date(printer.lastUpdated).getTime();
    const fiveMinutesMs = 5 * 60 * 1000;
    return Date.now() - lastUpdateMs > fiveMinutesMs;
  };

  const shouldShowCleaningHangingWarning = printer => {
    if (!printer) return false;
    const lastUpdatedMs = printer?.lastUpdated ? new Date(printer.lastUpdated).getTime() : NaN;
    const isOfflineByPrinterTimestamp =
      !Number.isFinite(lastUpdatedMs) || Date.now() - lastUpdatedMs > 5 * 60 * 1000;
    return (
      printer.shouldStartCleaning === true &&
      (isOfflineByPrinterTimestamp || printer.connectionOk === false)
    );
  };

  const getStatusColor = printer => {
    if (isPrinterOffline(printer)) return 'error';
    if (!printer.connectionOk) return 'error';
    if (printer.cardsNearEmpty) return 'warning';
    if (printer.ribbonPercentage < 20) return 'warning';
    return 'success';
  };

  const getStatusIcon = printer => {
    if (isPrinterOffline(printer)) return <ErrorIcon />;
    if (!printer.connectionOk) return <ErrorIcon />;
    if (printer.cardsNearEmpty || printer.ribbonPercentage < 20) return <WarningIcon />;
    return <CheckCircleIcon />;
  };

  const getStatusText = printer => {
    if (isPrinterOffline(printer)) return 'Offline';
    if (!printer.connectionOk) return 'Verbindung fehlerhaft';
    if (printer.cardsNearEmpty) return 'Karten fast leer';
    if (printer.ribbonPercentage < 20) return 'Ribbon fast leer';
    return 'Online';
  };

  if (loading) {
    return (
      <Box
        sx={{
          ...viewRootTextPrimarySx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
        <Alert severity='error'>Fehler beim Laden der Drucker-Status: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4' component='h1' display='flex' alignItems='center' gap={1}>
          <PrintIcon />
          Drucker-Übersicht
        </Typography>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <IconButton
            variant='outlined'
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ minWidth: 'fit-content' }}
          >
            {refreshing ? 'Aktualisiere...' : 'Aktualisieren'}
          </Button>
        </Box>
      </Box>

      {printers.length === 0 ? (
        <Alert severity='info'>
          Keine Drucker gefunden. Erstellen Sie zuerst einen Drucker im Backend.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {printers.map(printer => (
            <Grid item xs={12} sm={6} key={printer.name}>
              <Card
                sx={{
                  height: '100%',
                  border: printer.shouldStartCleaning
                    ? `2px solid ${theme.palette.warning.main}`
                    : `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Box display='flex' justifyContent='space-between' alignItems='flex-start' mb={2}>
                    <Typography variant='h6' component='h2' noWrap>
                      {printer.name}
                    </Typography>
                    <Box display='flex' alignItems='center' gap={1}>
                      {canStartCleaning && (
                        <Tooltip title='Reinigung starten'>
                          <span>
                            <IconButton
                              size='small'
                              onClick={() => handleOpenCleaningDialog(printer)}
                              disabled={printer.shouldStartCleaning}
                              sx={{
                                opacity: 0.6,
                                '&:hover': {
                                  opacity: 1,
                                },
                              }}
                            >
                              <CleaningIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Chip
                        icon={getStatusIcon(printer)}
                        label={getStatusText(printer)}
                        color={getStatusColor(printer)}
                        size='small'
                        sx={{ marginLeft: '5px' }}
                      />
                    </Box>
                  </Box>

                  {shouldShowCleaningHangingWarning(printer) && (
                    <Alert
                      severity='error'
                      sx={{ mb: 2, fontWeight: '500' }}
                      action={
                        canStartCleaning ? (
                          <Button
                            size='small'
                            color='inherit'
                            onClick={() => handleOpenCancelCleaningDialog(printer)}
                            sx={{ mr: 1 }}
                          >
                            Reinigung abbrechen
                          </Button>
                        ) : null
                      }
                    >
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Drucker antwortet nicht - Reinigung hängt möglicherweise
                      </Typography>
                      <Typography component='div' sx={{ fontSize: '0.875rem' }}>
                        Bitte prüfe folgende Schritte:
                        <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                          <li>Das Farbband muss entfernt werden</li>
                          <li>Die Reinigungskarte muss eingelegt sein</li>
                          <li>Der Schieberegler für Kartenbreite muss auf das Maximum eingerichtet sein</li>
                        </ul>
                        Nachdem alle Schritte befolgt wurden, starte die Reinigung erneut.
                      </Typography>
                    </Alert>
                  )}
                  {printer.shouldStartCleaning && !shouldShowCleaningHangingWarning(printer) && (
                    <Alert
                      severity='warning'
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          size='small'
                          color='inherit'
                          onClick={() => handleOpenCancelCleaningDialog(printer)}
                          sx={{ mr: 1 }}
                        >
                          Reinigung abbrechen
                        </Button>
                      }
                    >
                      Reinigung läuft...
                    </Alert>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Karten bis zur nächsten Reinigung:{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {printer.cardsBeforeNextCleaning || 'N/A'}
                      </Box>{' '}
                      ({translateCleaningType(printer.nextCleaningType) || ''})
                    </Typography>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Karten fast aufgebraucht:{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {printer.cardsNearEmpty === true ? 'Ja' : 'Nein'}
                      </Box>
                    </Typography>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Farbband verbraucht in{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {printer.ribbonCount || 'N/A'}
                      </Box>{' '}
                      Karten (
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {printer.ribbonPercentage || 'N/A'}%
                      </Box>{' '}
                      verbleibend)
                    </Typography>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Karten bis zur nächsten kleinen Reinigung:{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {printer.cardsUntilSmallCleaning || 'N/A'}
                      </Box>
                    </Typography>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Karten bis zur nächsten großen Reinigung:{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {printer.cardsUntilBigCleaning || 'N/A'}
                      </Box>
                    </Typography>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Ingesamt durchgeführte kleine Reinigungen:{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {printer.smallCleaningsPerformed || 0}
                      </Box>
                    </Typography>
                    {printer.lastUpdated && (
                      <Typography variant='body2' color='text.secondary' gutterBottom>
                        Letzte Statusaktualisierung:{' '}
                        <Box component='span' sx={{ fontWeight: 600 }}>
                          {new Date(printer.lastUpdated).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          Uhr
                        </Box>
                      </Typography>
                    )}
                  </Box>

                  <Box display='flex' justifyContent='center' alignItems='center'>
                    {canStartCleaning &&
                      (printer.cardsBeforeNextCleaning < 100 || printer.shouldStartCleaning) && (
                        <Tooltip title='Reinigung starten'>
                          <IconButton
                            color='primary'
                            onClick={() => handleOpenCleaningDialog(printer)}
                            disabled={printer.shouldStartCleaning}
                            sx={{
                              width: '100%',
                              color: theme.palette.success.contrastText,
                              backgroundColor: 'success.main',
                              borderRadius: '5px',
                              '&:hover': {
                                backgroundColor: 'success.dark',
                              },
                            }}
                          >
                            <CleaningIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <PrinterCleaningGuideModal
        open={cleaningDialogOpen}
        onClose={handleCloseCleaningDialog}
        printerName={selectedPrinter?.name}
        onConfirmStartCleaning={handleConfirmCleaning}
        disabled={selectedPrinter?.shouldStartCleaning}
        isCleaningActive={selectedPrinter?.shouldStartCleaning === true}
      />
      <Dialog open={cancelCleaningDialogOpen} onClose={handleCloseCancelCleaningDialog}>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color='error' />
          Reinigung abbrechen
        </DialogTitle>
        <DialogContent>
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              WICHTIG: Neustart des Druckers erforderlich!
            </Typography>
            <Typography>
              Der Abbruch der Reinigung erfordert trotzdem einen Neustart des Druckers, damit der
              Drucker wieder ordnungsgemäß funktioniert.
            </Typography>
          </Alert>
          <Typography>
            Möchten Sie die Reinigung für{' '}
            <Box component='span' sx={{ fontWeight: 600 }}>
              {selectedPrinter?.name || '-'}
            </Box>{' '}
            wirklich abbrechen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelCleaningDialog}>Abbrechen</Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleConfirmCancelCleaning}
            autoFocus
          >
            Reinigung abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PrinterOverview;
